import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import PeopleIcon from '@mui/icons-material/People';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { UserManagementOverviewResponse, UserManagementRankedUser } from 'api/userCredentials';
import AdminAnalyticsBarChart from 'components/atoms/AdminAnalyticsBarChart';
import { find, map } from 'es-toolkit/compat';
import React from 'react';

import { TableEditingContextProvider } from 'components/atoms/EditableDataGrid/TableEditingContext';
import { RowSelectionContextProvider } from 'components/atoms/RowSelectionContext';
import PageHeader from 'components/PageHeader';
import { SITE_HEADER_NUMBER } from 'components/SiteHeader/SiteHeader';
import { getTranslucentSurfaceStyles } from 'components/theme/theme';
import { Permission } from 'interfaces/permissionOption';
import { useUserCredentialsFieldsContext } from 'interfaces/userCredentials/UserCredentialsFieldsContext';
import { BooleanParam, withDefault } from 'use-query-params';
import { useNavigateBackFromAdmin } from 'utils/adminNavigation';
import { formatDateTimeForUser } from 'utils/dateTime';
import { useQueryParamAsLocalState } from 'utils/statelessUpdateUrlParams';
import { usePermissions } from 'utils/usePermissions';
import { wrapAdminPage } from '../AdminPageLayout';
import { CurrentUserPermissionsAccordion } from './CurrentUserPermissionsAccordion';
import { RolesManagement } from './RolesManagement';
import UserActivityTimelineChart from './UserActivityTimelineChart';
import { UserCredentialsDataGrid, UserDirectorySearchField } from './UserCredentialsDataGrid';
import { useUserManagementOverview } from './useUserManagementOverview';

const userCredentialsEditingPermissions = [Permission.DefineRolesToLabUsers, Permission.ManageNucleaiUsers];

type UserManagementTab = 'overview' | 'directory' | 'permissions';

const isUserManagementTab = (value: string | null | undefined): value is UserManagementTab =>
  value === 'overview' || value === 'directory' || value === 'permissions';

const TAB_DESCRIPTIONS: Record<UserManagementTab, string> = {
  overview:
    'Track active users, internal versus external activity, active labs, and new study creation across recent time windows.',
  directory: 'Search users, edit assignments, and switch between lab and Nucleai directories from one admin workspace.',
  permissions: 'Review the permissions currently granted to your own signed-in account.',
};

const TAB_LABELS: Record<UserManagementTab, string> = {
  overview: 'Overview',
  directory: 'Directory',
  permissions: 'Your Access',
};

interface SummaryCardProps {
  label: string;
  value: string | number;
  helperText?: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, helperText }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ py: 1.75, px: 2, '&:last-child': { pb: 1.75 } }}>
      <Typography
        color="text.secondary"
        sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 700, lineHeight: 1.1, mt: 0.9 }}>{value}</Typography>
      {helperText ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {helperText}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

interface HeaderStatPillProps extends Pick<SummaryCardProps, 'label' | 'value'> {
  compact?: boolean;
}

const HeaderStatPill: React.FC<HeaderStatPillProps> = ({ label, value, compact = false }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        px: compact ? 1.1 : 1.35,
        py: compact ? 0.55 : 0.8,
        borderRadius: 999,
        border: 1,
        borderColor: alpha(theme.palette.divider, compact ? 0.22 : 0.16),
        backgroundColor:
          theme.palette.mode === 'light'
            ? alpha(theme.palette.common.white, compact ? 0.5 : 0.38)
            : alpha(theme.palette.common.black, compact ? 0.28 : 0.2),
        display: 'inline-flex',
        flexDirection: compact ? 'row' : 'column',
        alignItems: compact ? 'center' : 'flex-start',
        gap: compact ? 0.75 : 0.25,
        minWidth: compact ? 0 : 96,
        maxWidth: compact ? '100%' : 140,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        transition: theme.transitions.create(
          ['padding', 'gap', 'min-width', 'background-color', 'border-color', 'transform'],
          {
            duration: theme.transitions.duration.shorter,
          }
        ),
      }}
    >
      <Typography
        color="text.secondary"
        sx={{
          fontSize: compact ? 10 : 10.5,
          fontWeight: 700,
          letterSpacing: compact ? 0.35 : 0.45,
          textTransform: 'uppercase',
          lineHeight: 1.1,
          whiteSpace: compact ? 'nowrap' : 'normal',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: compact ? 12.5 : 14,
          fontWeight: 700,
          lineHeight: 1.1,
          whiteSpace: compact ? 'nowrap' : 'normal',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const UserRankingList: React.FC<{
  title: string;
  users: UserManagementRankedUser[];
  primaryMetricLabel: string;
  renderPrimaryMetric: (user: UserManagementRankedUser) => string;
  secondaryMetricLabel?: string;
  renderSecondaryMetric?: (user: UserManagementRankedUser) => string;
  maxListHeight?: number;
}> = ({
  title,
  users,
  primaryMetricLabel,
  renderPrimaryMetric,
  secondaryMetricLabel,
  renderSecondaryMetric,
  maxListHeight,
}) => (
  <Box>
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
      <Typography
        color="text.secondary"
        sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}
      >
        {title}
      </Typography>
      <Chip size="small" variant="outlined" label={`${users.length} users`} />
    </Stack>
    {users.length === 0 ? (
      <Box
        sx={{
          minHeight: 160,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Typography color="text.secondary">No users in this segment</Typography>
      </Box>
    ) : (
      <Box
        sx={{
          maxHeight: maxListHeight ?? 'none',
          overflowY: maxListHeight ? 'auto' : 'visible',
          pr: maxListHeight ? 0.5 : 0,
        }}
      >
        <Stack spacing={1.25}>
          {map(users, (user) => (
            <Box
              key={user.userId}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                px: { xs: 1.25, md: 1.5 },
                py: 1.25,
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.25}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography fontWeight={600} sx={{ lineHeight: 1.2 }}>
                    {user.userName || user.userEmail}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, wordBreak: 'break-word' }}>
                    {user.userEmail}
                  </Typography>
                  {user.lastLoginLocation || user.lastLoginCountry ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                      {user.lastLoginLocation || user.lastLoginCountry}
                    </Typography>
                  ) : null}
                </Box>
                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  flexWrap="wrap"
                  justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
                >
                  <Chip size="small" color="primary" label={`${primaryMetricLabel}: ${renderPrimaryMetric(user)}`} />
                  {secondaryMetricLabel && renderSecondaryMetric ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${secondaryMetricLabel}: ${renderSecondaryMetric(user)}`}
                    />
                  ) : null}
                </Stack>
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>
    )}
  </Box>
);

const UserSplitRankingCard: React.FC<{
  title: string;
  description: string;
  externalUsers: UserManagementRankedUser[];
  internalUsers: UserManagementRankedUser[];
  canViewInternalMetrics: boolean;
  primaryMetricLabel: string;
  renderPrimaryMetric: (user: UserManagementRankedUser) => string;
  secondaryMetricLabel?: string;
  renderSecondaryMetric?: (user: UserManagementRankedUser) => string;
  maxListHeight?: number;
}> = ({
  title,
  description,
  externalUsers,
  internalUsers,
  canViewInternalMetrics,
  primaryMetricLabel,
  renderPrimaryMetric,
  secondaryMetricLabel,
  renderSecondaryMetric,
  maxListHeight,
}) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ p: { xs: 2, md: 2.25 }, '&:last-child': { pb: { xs: 2, md: 2.25 } }, height: '100%' }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        {description}
      </Typography>
      <Stack spacing={2}>
        <UserRankingList
          title="External Users"
          users={externalUsers}
          primaryMetricLabel={primaryMetricLabel}
          renderPrimaryMetric={renderPrimaryMetric}
          secondaryMetricLabel={secondaryMetricLabel}
          renderSecondaryMetric={renderSecondaryMetric}
          maxListHeight={maxListHeight}
        />
        {canViewInternalMetrics ? (
          <UserRankingList
            title="Internal Users"
            users={internalUsers}
            primaryMetricLabel={primaryMetricLabel}
            renderPrimaryMetric={renderPrimaryMetric}
            secondaryMetricLabel={secondaryMetricLabel}
            renderSecondaryMetric={renderSecondaryMetric}
            maxListHeight={maxListHeight}
          />
        ) : null}
      </Stack>
    </CardContent>
  </Card>
);

const OverviewTab: React.FC<{
  data?: UserManagementOverviewResponse;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}> = ({ data, isLoading, isError, error }) => {
  const theme = useTheme();
  const [selectedTimelineKey, setSelectedTimelineKey] = React.useState('week');

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error">Failed to load user activity overview: {String(error)}</Alert>;
  }

  if (!data) {
    return null;
  }

  const selectedWindow = find(data.activityWindows, { key: selectedTimelineKey }) || data.activityWindows[0];
  const selectedTimeline = find(data.activityTimelines, { key: selectedTimelineKey }) || data.activityTimelines[0];
  const selectedRecentUsers =
    find(data.recentUsersByWindow, { key: selectedTimelineKey }) || data.recentUsersByWindow[0];
  const selectedMostUsedUsers =
    find(data.mostUsedUsersByWindow, { key: selectedTimelineKey }) || data.mostUsedUsersByWindow[0];
  const selectedActiveUsers =
    find(data.activeUsersByWindow, { key: selectedTimelineKey }) || data.activeUsersByWindow[0];
  const selectedStudies =
    find(data.recentStudiesByWindow, { key: selectedTimelineKey }) || data.recentStudiesByWindow[0];
  const recentStudies = selectedStudies?.studies || [];
  const windowLabels = map(data.activityWindows, 'label');

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent sx={{ p: { xs: 2, md: 2.25 }, '&:last-child': { pb: { xs: 2, md: 2.25 } } }}>
            <Grid container spacing={2} justifyContent="space-between" alignItems="center">
              <Grid item xs={12} md>
                <Typography variant="subtitle1" fontWeight={600}>
                  Engagement Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Timeline data is based on successful Auth0 login events. Each user is counted once per UTC day in the
                  activity trend, recent users are ranked by latest login, and most used users are ranked by active days
                  plus login volume in the selected period.
                </Typography>
              </Grid>
              <Grid item xs={12} md="auto">
                <ToggleButtonGroup
                  size="small"
                  color="primary"
                  value={selectedTimeline.key}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setSelectedTimelineKey(newValue);
                    }
                  }}
                  aria-label="User activity timeline range"
                >
                  <ToggleButton value="week">7 Days</ToggleButton>
                  <ToggleButton value="month">1 Month</ToggleButton>
                  <ToggleButton value="quarter">3 Months</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
              <Chip
                size="small"
                label={`${selectedWindow.activeUsers} active users`}
                color="primary"
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${selectedWindow.activeLabs} active labs`}
                color="success"
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${selectedWindow.newStudies} new studies`}
                color="warning"
                variant="outlined"
              />
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} xl={8}>
        <UserActivityTimelineChart
          points={selectedTimeline.points}
          canViewInternalMetrics={data.canViewInternalMetrics}
          title={`${selectedTimeline.label} User Activity Timeline`}
        />
      </Grid>
      <Grid item xs={12} xl={4}>
        <Card variant="outlined" sx={{ height: '100%' }}>
          <CardContent sx={{ p: { xs: 2, md: 2.25 }, '&:last-child': { pb: { xs: 2, md: 2.25 } }, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Recent Studies
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
              Newly created studies in {selectedStudies.label.toLowerCase()}, with the approved case and slide volume in
              each study.
            </Typography>
            {recentStudies.length === 0 ? (
              <Box sx={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">No studies created in this period</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {map(recentStudies, (study) => (
                  <ListItem key={study.studyId} disableGutters divider>
                    <ListItemText
                      primary={study.studyName}
                      secondary={
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.75, alignItems: 'center' }}>
                          <Chip size="small" label={`${study.casesCount} cases`} color="primary" variant="outlined" />
                          <Chip
                            size="small"
                            label={`${study.slidesCount} slides`}
                            color="secondary"
                            variant="outlined"
                          />
                          <Typography component="span" variant="body2" color="text.secondary">
                            {study.createdAt
                              ? formatDateTimeForUser(study.createdAt, { dateStyle: 'medium' })
                              : 'No date'}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={3}>
          <Grid item xs={12} xl={4}>
            <UserSplitRankingCard
              title="Recent Users"
              description={`Ranked by the latest successful Auth0 login during ${selectedRecentUsers.label.toLowerCase()}.`}
              externalUsers={selectedRecentUsers.externalUsers}
              internalUsers={selectedRecentUsers.internalUsers}
              canViewInternalMetrics={data.canViewInternalMetrics}
              primaryMetricLabel="Last Login"
              renderPrimaryMetric={(user) =>
                user.lastLogin
                  ? formatDateTimeForUser(user.lastLogin, {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })
                  : 'Never'
              }
              secondaryMetricLabel="Active Days"
              renderSecondaryMetric={(user) => user.activeDays.toLocaleString()}
            />
          </Grid>
          <Grid item xs={12} xl={4}>
            <UserSplitRankingCard
              title="Most Used Users"
              description={`Ranked by unique active days and login-event volume during ${selectedMostUsedUsers.label.toLowerCase()}.`}
              externalUsers={selectedMostUsedUsers.externalUsers}
              internalUsers={selectedMostUsedUsers.internalUsers}
              canViewInternalMetrics={data.canViewInternalMetrics}
              primaryMetricLabel="Active Days"
              renderPrimaryMetric={(user) => user.activeDays.toLocaleString()}
              secondaryMetricLabel="Login Events"
              renderSecondaryMetric={(user) => user.loginEvents.toLocaleString()}
            />
          </Grid>
          <Grid item xs={12} xl={4}>
            <UserSplitRankingCard
              title="Active Users"
              description={`All users with at least one successful Auth0 login during ${selectedActiveUsers.label.toLowerCase()}.`}
              externalUsers={selectedActiveUsers.externalUsers}
              internalUsers={selectedActiveUsers.internalUsers}
              canViewInternalMetrics={data.canViewInternalMetrics}
              primaryMetricLabel="Active Days"
              renderPrimaryMetric={(user) => user.activeDays.toLocaleString()}
              secondaryMetricLabel="Login Events"
              renderSecondaryMetric={(user) => user.loginEvents.toLocaleString()}
              maxListHeight={420}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} lg={8}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={data.canViewInternalMetrics ? 4 : 6}>
            <SummaryCard
              label={`${selectedWindow.label} Active Users`}
              value={selectedWindow.activeUsers}
              helperText="Users with at least one successful login event during the selected period."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={data.canViewInternalMetrics ? 4 : 6}>
            <SummaryCard
              label={`${selectedWindow.label} External Users`}
              value={selectedWindow.activeExternalUsers}
              helperText="Lab users active in the selected period."
            />
          </Grid>
          {data.canViewInternalMetrics ? (
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard
                label={`${selectedWindow.label} Internal Users`}
                value={selectedWindow.activeInternalUsers}
                helperText="Nucleai users active in the selected period."
              />
            </Grid>
          ) : null}
          <Grid item xs={12} sm={6} md={6}>
            <SummaryCard
              label={`${selectedWindow.label} Active Labs`}
              value={selectedWindow.activeLabs}
              helperText="Labs with at least one external user sign-in in this range."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <SummaryCard
              label={`${selectedWindow.label} New Studies`}
              value={selectedWindow.newStudies}
              helperText="Studies created during the selected period."
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={6}>
        <AdminAnalyticsBarChart
          labels={windowLabels}
          values={map(data.activityWindows, 'activeLabs')}
          title="Active Labs by Range"
          color={theme.palette.success.main}
          height={320}
          horizontal
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <AdminAnalyticsBarChart
          labels={windowLabels}
          values={map(data.activityWindows, 'newStudies')}
          title="New Studies by Range"
          color={theme.palette.warning.main}
          height={320}
          horizontal
        />
      </Grid>
    </Grid>
  );
};

export const UserCredentialsRolesAndPermissions = wrapAdminPage(() => {
  const theme = useTheme();
  const navigateBackFromAdmin = useNavigateBackFromAdmin();
  const userCredentialsFieldsContext = useUserCredentialsFieldsContext();
  const { labId, permissions, roles, isLoadingRoles, isLoadingPermissions } = userCredentialsFieldsContext;
  const { hasPermission, hasSomePermissions, hasEveryPermission } = usePermissions();
  const canManageUsers = hasSomePermissions(userCredentialsEditingPermissions);
  const canSwitchDirectory = hasEveryPermission(userCredentialsEditingPermissions);

  const [manageNucleaiUsers, setManageNucleaiUsers] = useQueryParamAsLocalState(
    'manageNucleaiUsers',
    withDefault(BooleanParam, hasPermission(Permission.ManageNucleaiUsers))
  );

  const [showRolesManagement, setShowRolesManagement] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<UserManagementTab>('overview');
  const [directorySearch, setDirectorySearch] = React.useState('');
  const [hasStickyHeaderShadow, setHasStickyHeaderShadow] = React.useState(false);
  const [isStickyHeaderCompact, setIsStickyHeaderCompact] = React.useState(false);
  const stickyChromeRef = React.useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = React.useRef<Window | HTMLElement | null>(null);
  const tabScrollPositionsRef = React.useRef<Record<UserManagementTab, number>>({
    overview: 0,
    directory: 0,
    permissions: 0,
  });
  const pendingScrollRestoreTabRef = React.useRef<UserManagementTab | null>(null);

  const overviewQuery = useUserManagementOverview(labId, canManageUsers);

  const findScrollParent = (element: HTMLElement | null): Window | HTMLElement => {
    let current: HTMLElement | null = element?.parentElement ?? null;

    while (current) {
      const { overflowY } = window.getComputedStyle(current);

      if (/(auto|scroll|overlay)/.test(overflowY) && current.scrollHeight > current.clientHeight) {
        return current;
      }

      current = current.parentElement;
    }

    return window;
  };

  React.useEffect(() => {
    const stickyChrome = stickyChromeRef.current;

    if (!stickyChrome) {
      return;
    }

    const scrollParent = findScrollParent(stickyChrome);
    scrollContainerRef.current = scrollParent;
    const updateStickyChrome = () => {
      const scrollTop = scrollParent instanceof Window ? scrollParent.scrollY : scrollParent.scrollTop;

      setHasStickyHeaderShadow(scrollTop > 8);
      setIsStickyHeaderCompact(scrollTop > 48);
    };

    updateStickyChrome();
    scrollParent.addEventListener('scroll', updateStickyChrome, { passive: true });

    return () => {
      scrollParent.removeEventListener('scroll', updateStickyChrome);
    };
  }, []);

  React.useEffect(() => {
    if (pendingScrollRestoreTabRef.current !== activeTab) {
      return;
    }

    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer) {
      pendingScrollRestoreTabRef.current = null;
      return;
    }

    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        const nextScrollTop = tabScrollPositionsRef.current[activeTab] ?? 0;

        if (scrollContainer instanceof Window) {
          scrollContainer.scrollTo({ top: nextScrollTop });
        } else {
          scrollContainer.scrollTop = nextScrollTop;
        }

        pendingScrollRestoreTabRef.current = null;
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
    };
  }, [activeTab]);

  const visibleTab = canManageUsers ? activeTab : 'permissions';
  const showCompactDirectorySearch = visibleTab === 'directory' && canManageUsers && isStickyHeaderCompact;
  const compactHeaderSubtitle = TAB_LABELS[visibleTab];
  const currentAudience = manageNucleaiUsers ? 'Nucleai Users' : 'Lab Users';
  const overviewSummary = overviewQuery.data?.summary;
  const stickyChromeTransition = theme.transitions.create(
    ['padding-top', 'padding-bottom', 'margin-top', 'max-height', 'opacity', 'transform', 'box-shadow'],
    {
      duration: theme.transitions.duration.shorter,
    }
  );

  return (
    <Box sx={{ pb: { xs: 2, md: 2.5 } }}>
      <RolesManagement isOpen={showRolesManagement} onClose={() => setShowRolesManagement(false)} />

      <Box
        ref={stickyChromeRef}
        sx={{
          position: 'sticky',
          top: SITE_HEADER_NUMBER,
          zIndex: theme.zIndex.appBar - 1,
          mb: 3,
          width: '100vw',
          ml: 'calc(50% - 50vw)',
          mr: 'calc(50% - 50vw)',
          ...getTranslucentSurfaceStyles(theme),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          borderRadius: 0,
          boxShadow: hasStickyHeaderShadow
            ? `0 8px 16px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.16 : 0.045)}`
            : 'none',
          transition: theme.transitions.create('box-shadow', {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Box
          sx={{
            maxWidth: `${theme.breakpoints.values.lg}px`,
            mx: 'auto',
            px: { xs: 2, sm: 3 },
            pt: isStickyHeaderCompact ? 1.5 : 3,
            pb: isStickyHeaderCompact ? 1.25 : visibleTab === 'directory' && canManageUsers ? 2.5 : 2,
            transition: stickyChromeTransition,
          }}
        >
          <Grid container spacing={2} justifyContent="space-between" alignItems="center">
            <Grid item xs={12} lg={6}>
              <PageHeader
                pageTitle="User Management"
                pageSubtitle={
                  <Box
                    sx={{
                      mt: isStickyHeaderCompact ? 0 : 0.5,
                      maxWidth: 860,
                      overflow: 'hidden',
                      maxHeight: isStickyHeaderCompact ? 22 : 48,
                      opacity: 1,
                      transform: 'translateY(0)',
                      transition: stickyChromeTransition,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {isStickyHeaderCompact
                        ? compactHeaderSubtitle
                        : 'Manage user access, monitor engagement, and review study intake from one admin dashboard.'}
                    </Typography>
                  </Box>
                }
                leadingAction={
                  <Tooltip title="Back" placement="top" arrow>
                    <IconButton aria-label="Back" onClick={navigateBackFromAdmin} sx={{ mr: 2 }}>
                      <ArrowBackRoundedIcon />
                    </IconButton>
                  </Tooltip>
                }
              />
            </Grid>
            {canManageUsers ? (
              <Grid item xs={12} lg={6}>
                <Box
                  sx={{
                    overflow: 'hidden',
                    opacity: 1,
                    transform: isStickyHeaderCompact ? 'translateY(-2px)' : 'translateY(0)',
                    transition: stickyChromeTransition,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={isStickyHeaderCompact ? 0.75 : 1}
                    useFlexGap
                    flexWrap="wrap"
                    justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
                    sx={{
                      transition: stickyChromeTransition,
                    }}
                  >
                    <HeaderStatPill
                      compact={isStickyHeaderCompact}
                      label="Analytics Scope"
                      value={overviewSummary?.scopeLabel ?? 'Loading...'}
                    />
                    <HeaderStatPill
                      compact={isStickyHeaderCompact}
                      label="Users"
                      value={overviewSummary?.totalUsers ?? '...'}
                    />
                    <HeaderStatPill
                      compact={isStickyHeaderCompact}
                      label="Studies"
                      value={overviewSummary?.totalStudies ?? '...'}
                    />
                    <HeaderStatPill
                      compact={isStickyHeaderCompact}
                      label="Cases"
                      value={overviewSummary?.totalCases ?? '...'}
                    />
                    <HeaderStatPill
                      compact={isStickyHeaderCompact}
                      label="Slides"
                      value={overviewSummary?.totalSlides ?? '...'}
                    />
                  </Stack>
                </Box>
              </Grid>
            ) : null}
          </Grid>

          {canManageUsers ? (
            <Box
              sx={{
                mt: isStickyHeaderCompact ? 0.75 : 2,
                overflow: 'hidden',
                maxHeight: isStickyHeaderCompact ? 0 : 56,
                opacity: isStickyHeaderCompact ? 0 : 1,
                transform: isStickyHeaderCompact ? 'translateY(-8px)' : 'translateY(0)',
                transition: stickyChromeTransition,
              }}
            >
              <Tabs
                value={visibleTab}
                onChange={(_, value) => {
                  if (isUserManagementTab(value)) {
                    const scrollContainer = scrollContainerRef.current ?? findScrollParent(stickyChromeRef.current);
                    const currentScrollTop =
                      scrollContainer instanceof Window ? scrollContainer.scrollY : scrollContainer.scrollTop;

                    scrollContainerRef.current = scrollContainer;
                    tabScrollPositionsRef.current[activeTab] = currentScrollTop;
                    pendingScrollRestoreTabRef.current = value;
                    setActiveTab(value);
                  }
                }}
                variant="scrollable"
              >
                <Tab value="overview" label={TAB_LABELS.overview} icon={<PeopleIcon />} iconPosition="start" />
                <Tab value="directory" label={TAB_LABELS.directory} icon={<PeopleIcon />} iconPosition="start" />
                <Tab
                  value="permissions"
                  label={TAB_LABELS.permissions}
                  icon={<AdminPanelSettingsRoundedIcon />}
                  iconPosition="start"
                />
              </Tabs>
            </Box>
          ) : null}

          <Box
            sx={{
              mt: isStickyHeaderCompact ? 0.5 : 2,
              maxWidth: 960,
              overflow: 'hidden',
              maxHeight: isStickyHeaderCompact ? 0 : 56,
              opacity: isStickyHeaderCompact ? 0 : 1,
              transform: isStickyHeaderCompact ? 'translateY(-6px)' : 'translateY(0)',
              transition: stickyChromeTransition,
            }}
          >
            <Typography variant="body1" color="text.secondary">
              {TAB_DESCRIPTIONS[visibleTab]}
            </Typography>
          </Box>

          {visibleTab === 'directory' && canManageUsers ? (
            <Box
              sx={{
                mt: isStickyHeaderCompact ? 1 : 2,
                pt: isStickyHeaderCompact ? 1 : 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                transition: stickyChromeTransition,
              }}
            >
              <Grid container spacing={2} justifyContent="space-between" alignItems="center">
                {!isStickyHeaderCompact ? (
                  <Grid item xs={12} md>
                    <Box
                      sx={{
                        overflow: 'hidden',
                        maxHeight: 88,
                        opacity: 1,
                        transform: 'translateY(0)',
                        transition: stickyChromeTransition,
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={600}>
                        Directory Controls
                      </Typography>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                        <Chip size="small" label={`Audience: ${currentAudience}`} color="primary" variant="outlined" />
                        <Chip
                          size="small"
                          label={`Roles: ${isLoadingRoles ? 'Loading...' : roles.length.toLocaleString()}`}
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          label={`Permissions: ${
                            isLoadingPermissions ? 'Loading...' : permissions.length.toLocaleString()
                          }`}
                          variant="outlined"
                        />
                      </Stack>
                    </Box>
                  </Grid>
                ) : null}
                <Grid item xs={12} md={isStickyHeaderCompact ? 12 : 'auto'}>
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                    justifyContent="flex-end"
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    sx={{
                      width: '100%',
                      ml: { md: isStickyHeaderCompact ? 'auto' : 0 },
                    }}
                  >
                    {showCompactDirectorySearch ? (
                      <Box
                        sx={{
                          flex: { xs: '1 1 100%', md: '1 1 320px' },
                          minWidth: 0,
                          maxWidth: { md: 420 },
                        }}
                      >
                        <UserDirectorySearchField value={directorySearch} onChange={setDirectorySearch} compact />
                      </Box>
                    ) : null}
                    {canSwitchDirectory ? (
                      <Box sx={{ flexShrink: 0, ml: { md: showCompactDirectorySearch ? 'auto' : 0 } }}>
                        <ToggleButtonGroup
                          size="small"
                          color="primary"
                          value={manageNucleaiUsers ? 'nucleaiUsers' : 'labUsers'}
                          exclusive
                          onChange={(_, newValue) => {
                            if (!newValue) {
                              return;
                            }
                            setManageNucleaiUsers(newValue === 'nucleaiUsers');
                          }}
                          aria-label="User directory scope"
                        >
                          <ToggleButton value="labUsers">Lab Users</ToggleButton>
                          <ToggleButton value="nucleaiUsers">Nucleai Users</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    ) : null}
                    <Box sx={{ flexShrink: 0 }}>
                      <Button
                        disabled={isLoadingRoles}
                        variant="contained"
                        onClick={() => setShowRolesManagement(true)}
                      >
                        Manage Roles
                      </Button>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          ) : null}
        </Box>
      </Box>

      <Grid container direction="column" spacing={2}>
        {visibleTab === 'overview' && canManageUsers ? (
          <Grid item width="100%">
            <OverviewTab
              data={overviewQuery.data}
              isLoading={overviewQuery.isLoading}
              isError={overviewQuery.isError}
              error={overviewQuery.error}
            />
          </Grid>
        ) : null}

        {visibleTab === 'directory' && canManageUsers ? (
          <>
            <Grid item width="100%">
              <RowSelectionContextProvider>
                <TableEditingContextProvider fieldsContext={userCredentialsFieldsContext}>
                  <UserCredentialsDataGrid
                    manageNucleaiUsers={manageNucleaiUsers}
                    directorySearch={directorySearch}
                    onDirectorySearchChange={setDirectorySearch}
                  />
                </TableEditingContextProvider>
              </RowSelectionContextProvider>
            </Grid>
          </>
        ) : null}

        {visibleTab === 'permissions' ? (
          <Grid item width="100%">
            <CurrentUserPermissionsAccordion permissions={permissions} />
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
});
